import {
  useState,
  useEffect,
  createContext,
  useMemo,
  useCallback,
  useContext,
} from "react";
import axios from "axios";
import { ROOT_SERVER, MATERIAL_URL, BRAND_INFO_URLS, buildApiUrl } from "../utilities/Static";
import { authHeaders, markSignInForRenewal } from "../utilities/AuthState";
import SortFunction from "../utilities/SortFunction";
import { collectBrandArticleSlugs } from "../utilities/brandData";
import {
  canFilterBrandArticles,
  getBrandArticleFilterLabel,
  getSearchCustomerBrand,
  getSessionCustomerBrand,
} from "../utilities/loginAccount";
import { UserContext } from "./UserContext";
import { isVendorUser } from "../utilities/AuthState";
import {
  DEFAULT_PRICE_MAX,
  DEFAULT_PRICE_MIN,
  getAdminSettings,
} from "../utilities/adminSettings";

import { useQuery } from "@tanstack/react-query";

export const MaterialContext = createContext();

function readPriceBounds() {
  if (typeof window === "undefined") {
    return { min: DEFAULT_PRICE_MIN, max: DEFAULT_PRICE_MAX };
  }
  const settings = getAdminSettings();
  return { min: settings.priceMin, max: settings.priceMax };
}

const MaterialContextProvider = ({ children }) => {
  const { userInformation, changelogOutUser } = useContext(UserContext);
  const [nikeData, setNikeData] = useState([]);
  const [imageGrant, setImageGrant] = useState("");
  const [search, setSearch] = useState("");
  ///Filters
  const [isFilter, setIsFilter] = useState(false);
  const [priceBounds, setPriceBounds] = useState(readPriceBounds);
  const [priceRange, setPriceRange] = useState(() => {
    const bounds = readPriceBounds();
    return [{ min: bounds.min, max: bounds.max }];
  });
  const [status, setStatus] = useState("commercial");
  const [orderBy, setOrderBy] = useState("modified");
  const [brandArticlesOnly, setBrandArticlesOnly] = useState(false);

  const { isError, error, isSuccess, data, refetch } = useQuery({
    queryKey: ["materials"],
    queryFn: () => axios.get(MATERIAL_URL, { headers: authHeaders() }),
    enabled: Boolean(userInformation.isLoggedIn),
  });

  useEffect(() => {
    if (error?.response?.status !== 401) {
      return;
    }
    markSignInForRenewal();
    changelogOutUser();
  }, [changelogOutUser, error]);

  const allMaterials = useMemo(() => {
    return Array.isArray(data?.data) ? data.data : [];
  }, [data]);

  const materialsLoaded = !isError && isSuccess;
  const catalogError = Boolean(isError);

  const sessionBrand = getSessionCustomerBrand(userInformation);
  const brandArticleSlugs = useMemo(
    () => collectBrandArticleSlugs(nikeData, sessionBrand),
    [nikeData, sessionBrand],
  );
  const brandArticleFilterAvailable =
    canFilterBrandArticles(userInformation) && brandArticleSlugs.size > 0;
  const brandArticleFilterLabel = getBrandArticleFilterLabel(userInformation);
  const searchBrand = getSearchCustomerBrand(search, sessionBrand);

  const materials = useMemo(() => {
    const useBrandSlugs =
      (brandArticlesOnly && brandArticleFilterAvailable) || Boolean(searchBrand);
    const brandSlugs = useBrandSlugs
      ? collectBrandArticleSlugs(nikeData, searchBrand || sessionBrand)
      : null;

    return SortFunction(
      searchBrand ? "" : search,
      allMaterials,
      orderBy,
      priceRange,
      useBrandSlugs ? "none" : status,
      brandSlugs,
      !isVendorUser(userInformation),
    );
  }, [
    search,
    searchBrand,
    allMaterials,
    orderBy,
    priceRange,
    status,
    brandArticlesOnly,
    brandArticleFilterAvailable,
    nikeData,
    sessionBrand,
    userInformation,
  ]);

  const materialById = useMemo(() => {
    const map = new Map();
    allMaterials.forEach((material) => {
      if (material?.id != null) {
        map.set(material.id, material);
      }
    });
    return map;
  }, [allMaterials]);

  const nikeDataBySlug = useMemo(() => {
    const map = new Map();
    nikeData.forEach((item) => {
      if (!item?.slug) {
        return;
      }

      const existing = map.get(item.slug);
      if (existing) {
        existing.push(item);
      } else {
        map.set(item.slug, [item]);
      }
    });
    return map;
  }, [nikeData]);

  useEffect(() => {
    const remoteBrand = BRAND_INFO_URLS.some((url) => /^https?:/i.test(url));
    if (remoteBrand && !userInformation.isLoggedIn) {
      setNikeData([]);
      return undefined;
    }

    let cancelled = false;
    Promise.all(
      BRAND_INFO_URLS.map((url) =>
        axios
          .get(url, remoteBrand ? { headers: authHeaders() } : undefined)
          .then((response) => response.data)
          .catch(() => []),
      ),
    ).then((chunks) => {
      if (cancelled) {
        return;
      }
      setNikeData(
        chunks.flatMap((chunk) => (Array.isArray(chunk) ? chunk : [])),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [userInformation.isLoggedIn]);

  useEffect(() => {
    if (!userInformation.isLoggedIn) {
      setImageGrant("");
      return undefined;
    }

    let cancelled = false;
    const loadGrant = () => {
      axios
        .get(buildApiUrl("/upload/image-grant"), { headers: authHeaders() })
        .then((response) => {
          if (!cancelled) {
            setImageGrant(response.data?.grant || "");
          }
        })
        .catch(() => {
          if (!cancelled) {
            setImageGrant("");
          }
        });
    };

    loadGrant();
    const timer = window.setInterval(loadGrant, 8 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [userInformation.isLoggedIn]);

  useEffect(() => {
    if (!brandArticleFilterAvailable && brandArticlesOnly) {
      setBrandArticlesOnly(false);
    }
  }, [brandArticleFilterAvailable, brandArticlesOnly]);

  const changeStatus = useCallback((value) => {
    setStatus(value);
  }, []);

  const changeSearch = useCallback((value) => {
    setSearch(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearch("");
    setPriceRange([{ min: priceBounds.min, max: priceBounds.max }]);
    setIsFilter(status !== "commercial" || brandArticlesOnly);
  }, [brandArticlesOnly, priceBounds.max, priceBounds.min, status]);

  const changeOrderBy = useCallback((value) => {
    setOrderBy(value);
  }, []);

  const changePriceRange = useCallback((value) => {
    setPriceRange(value);
  }, []);

  const changePriceBounds = useCallback((min, max) => {
    const nextMin = Number(min);
    const nextMax = Number(max);
    if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax) || nextMin >= nextMax) {
      return;
    }
    setPriceBounds({ min: nextMin, max: nextMax });
    setPriceRange([{ min: nextMin, max: nextMax }]);
    setIsFilter(false);
  }, []);

  const changeIsFilter = useCallback((value) => {
    setIsFilter(value);
  }, []);

  const changeBrandArticlesOnly = useCallback((value) => {
    setBrandArticlesOnly(Boolean(value));
  }, []);

  const value = useMemo(
    () => ({
      allMaterials,
      materialById,
      materialsLoaded,
      catalogError,
      nikeData,
      nikeDataBySlug,
      imageGrant,
      ROOT_SERVER,
      materials,
      status,
      refetch,
      changeStatus,
      search,
      changeSearch,
      clearSearch,
      orderBy,
      changeOrderBy,
      priceRange,
      priceBounds,
      changePriceRange,
      changePriceBounds,
      isFilter,
      changeIsFilter,
      brandArticlesOnly,
      brandArticleFilterAvailable,
      brandArticleFilterLabel,
      changeBrandArticlesOnly,
    }),
    [
      allMaterials,
      materialById,
      materialsLoaded,
      catalogError,
      nikeData,
      nikeDataBySlug,
      imageGrant,
      materials,
      status,
      refetch,
      changeStatus,
      search,
      changeSearch,
      clearSearch,
      orderBy,
      changeOrderBy,
      priceRange,
      priceBounds,
      changePriceRange,
      changePriceBounds,
      isFilter,
      changeIsFilter,
      brandArticlesOnly,
      brandArticleFilterAvailable,
      brandArticleFilterLabel,
      changeBrandArticlesOnly,
    ],
  );

  return (
    <MaterialContext.Provider value={value}>
      {children}
    </MaterialContext.Provider>
  );
};

export default MaterialContextProvider;
